// Consultas GraphQL al motor pidiendo SOLO los campos que usa la pantalla. REST (/rest) devuelve registros completos
// (con relaciones enteras): para listas grandes como casos pesaba mas de 1 MB por carga.
import { ApiError, request } from './http';

type Pagina<T> = { edges: { node: T }[]; pageInfo: { hasNextPage: boolean; endCursor: string | null } };

export async function gqlTodos<T>(objeto: string, campos: string, opciones: { filtro?: unknown; orden?: unknown; max?: number } = {}): Promise<T[]> {
  const out: T[] = [];
  let despues: string | null = null;
  const max = opciones.max ?? 2000;
  do {
    const r: { data?: Record<string, Pagina<T>>; errors?: { message: string }[] } = await request('/graphql', {
      method: 'POST',
      body: {
        query: `query($f:${tipoFiltro(objeto)}, $o:[${tipoOrden(objeto)}], $n:Int, $a:String){ ${objeto}(filter:$f, orderBy:$o, first:$n, after:$a){ edges{ node{ ${campos} } } pageInfo{ hasNextPage endCursor } } }`,
        variables: { f: opciones.filtro ?? {}, o: opciones.orden ?? [], n: Math.min(200, max - out.length), a: despues },
      },
    });
    if (r.errors?.length) throw new ApiError(400, r.errors[0].message);
    const p = r.data?.[objeto];
    if (!p) break;
    out.push(...p.edges.map((e) => e.node));
    despues = p.pageInfo.hasNextPage ? p.pageInfo.endCursor : null;
  } while (despues && out.length < max);
  return out;
}

// Nombres de tipos de Twenty: objeto "casos" -> CasoFilterInput / CasoOrderByInput (singular con mayuscula inicial).
const SINGULAR: Record<string, string> = { casos: 'Caso', licencias3Cx: 'Licencia3Cx', messages: 'Message', llamadas: 'Llamada' };
const tipoFiltro = (o: string) => `${SINGULAR[o] ?? o}FilterInput`;
const tipoOrden = (o: string) => `${SINGULAR[o] ?? o}OrderByInput`;

/* ---------------- paginas con cursores (lista de casos con orden y paginas) ---------------- */

type Borde<T> = { edges: { cursor: string; node?: T }[]; pageInfo: { hasNextPage?: boolean; hasPreviousPage?: boolean; startCursor?: string | null; endCursor: string | null } };
type Tramo = { n: number; despues?: string | null; antes?: string | null; atras?: boolean };

async function tramo<T>(objeto: string, nodo: string, filtro: unknown, orden: unknown, t: Tramo): Promise<Borde<T>> {
  const r: { data?: Record<string, Borde<T>>; errors?: { message: string }[] } = await request('/graphql', {
    method: 'POST',
    body: {
      query: `query($f:${tipoFiltro(objeto)}, $o:[${tipoOrden(objeto)}], $n:Int, $a:String, $b:String){ ${objeto}(filter:$f, orderBy:$o, ${t.atras ? 'last:$n, before:$b' : 'first:$n, after:$a'}){ edges{ cursor ${nodo} } pageInfo{ hasNextPage hasPreviousPage startCursor endCursor } } }`,
      variables: { f: filtro ?? {}, o: orden ?? [], n: t.n, a: t.despues ?? null, b: t.antes ?? null },
    },
  });
  if (r.errors?.length) throw new ApiError(400, r.errors[0].message);
  return r.data?.[objeto] ?? { edges: [], pageInfo: { endCursor: null } };
}

// Filas con su cursor, en el orden de la consulta. Hacia adelante (`despues`, null = desde el principio) o hacia atras
// (`antes`: las `n` filas anteriores a ese cursor). Pide de a 200 (el motor no da mas por consulta) y cuenta lo que llega
// de verdad, por si el motor recorta. `campos` vacio: solo los cursores (recorridos livianos).
export async function gqlFilas<T>(objeto: string, campos: string, o: { filtro?: unknown; orden?: unknown; n: number; despues?: string | null; antes?: string | null }) {
  const nodo = campos ? `node{ ${campos} }` : '';
  const out: { cursor: string; node?: T }[] = [];
  const atras = o.antes !== undefined && o.antes !== null;
  let a = o.despues ?? null;
  let b = o.antes ?? null;
  let hayMas = true;
  while (out.length < o.n && hayMas) {
    const p: Borde<T> = await tramo<T>(objeto, nodo, o.filtro, o.orden, { n: Math.min(200, o.n - out.length), despues: a, antes: b, atras });
    if (atras) {
      out.unshift(...p.edges);
      b = p.pageInfo.startCursor ?? p.edges[0]?.cursor ?? null;
      hayMas = !!p.pageInfo.hasPreviousPage && p.edges.length > 0 && !!b;
    } else {
      out.push(...p.edges);
      a = p.pageInfo.endCursor ?? p.edges.at(-1)?.cursor ?? null;
      hayMas = !!p.pageInfo.hasNextPage && p.edges.length > 0 && !!a;
    }
  }
  return out;
}
